import { Stack } from "@mui/material";
import {
  Button,
  ButtonProps,
  EmptyState,
  LoadingIndicator,
  useSpacing,
} from "@surya-digital/leo-reactjs-material-ui";
import { KeyRound, Plus } from "lucide-react";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeleteAPIKeyRPC } from "@pulse/pulse-rpcs/lib/external-public-apis/deleteApiKeyRPC";
import { GetProjectsByAPIKeyRPC } from "@pulse/pulse-rpcs/lib/external-public-apis/getProjectsByApiKeyRPC";
import { InvalidateAndRotateAPIKeyRPC } from "@pulse/pulse-rpcs/lib/external-public-apis/invalidateAndRotateApiKeyRPC";
import { DetailSectionComponent } from "../../../components/DetailSectionComponent";
import { HeaderProps } from "../../../components/Header";
import { ErrorDialog } from "../../../components/dialogs/ErrorDialog";
import { UnexpectedErrorDialog } from "../../../components/dialogs/UnexpectedErrorDialog";
import { navigateOrGetNetworkErrorPage } from "../../../utility/navigateOrGetNetworkErrorPage";
import { useNetworkingErrorStore } from "../../networking/store/hooks";
import { useSignInStore } from "../../store/Hooks";
import { UserPrivileges } from "../../store/user/UserPrivileges";
import { useRouteNavigator } from "../../../routes/RoutesHelper";
import { APIKeysTable } from "../components/APIKeysTable";
import {
  APIKeyProjectSelectionDialog,
  APIKeyProjectSelectionMode,
} from "../components/APIKeyProjectSelectionDialog";
import {
  ConfirmAPIKeyAction,
  ConfirmAPIKeyActionDialog,
} from "../components/ConfirmAPIKeyActionDialog";
import { OneTimeCredentialsDialog } from "../components/OneTimeCredentialsDialog";
import { OneTimeAPICredentials } from "../stores/APIKeyManagementStore";
import { useAPIKeyManagementStore } from "../stores/Hooks";

type InformationalError = "staleKey" | "invalidProject";

export const APIKeyManagementPage: React.FC = observer(() => {
  const [selectionMode, setSelectionMode] =
    useState<APIKeyProjectSelectionMode>();
  const [editingAPIKeyId, setEditingAPIKeyId] = useState<string>();
  const [initialProjectIds, setInitialProjectIds] = useState<string[]>([]);
  const [loadingProjectAssignments, setLoadingProjectAssignments] =
    useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    action: ConfirmAPIKeyAction;
    apiKeyId: string;
  }>();
  const [credentials, setCredentials] = useState<OneTimeAPICredentials>();
  const [informationalError, setInformationalError] =
    useState<InformationalError>();
  const [unexpectedErrorMessage, setUnexpectedErrorMessage] =
    useState<string>();
  const [initialLoadFailed, setInitialLoadFailed] = useState(false);

  const { t } = useTranslation();
  const spacing = useSpacing();
  const navigateTo = useRouteNavigator();
  const signInStore = useSignInStore();
  const networkingErrorStore = useNetworkingErrorStore();
  const store = useAPIKeyManagementStore();
  const hasPrivilege = signInStore.isPrivilegeGranted(
    UserPrivileges.processExternalAPIKeys,
  );

  useEffect(() => {
    if (!hasPrivilege) navigateTo.pageNotFound();
  }, [hasPrivilege, navigateTo]);

  useEffect(() => {
    if (!hasPrivilege) return;
    Promise.all([store.getAPIKeys(), store.getActiveProjects()]).catch(() =>
      setInitialLoadFailed(true),
    );
  }, [hasPrivilege, store]);

  useEffect(
    () => () => {
      setCredentials(undefined);
      store.resetStore();
      networkingErrorStore.removeError();
    },
    [networkingErrorStore, store],
  );

  const closeSelectionDialog = (): void => {
    setSelectionMode(undefined);
    setEditingAPIKeyId(undefined);
    setInitialProjectIds([]);
    store.clearRPCError();
  };

  const handleStaleKey = async (): Promise<void> => {
    closeSelectionDialog();
    setConfirmAction(undefined);
    await store.refreshCurrentPage();
    setInformationalError("staleKey");
  };

  const handleEditAssignments = async (apiKeyId: string): Promise<void> => {
    setEditingAPIKeyId(apiKeyId);
    setInitialProjectIds([]);
    setLoadingProjectAssignments(true);
    setSelectionMode("edit");
    try {
      const projectIds = await store.getProjectsByAPIKey(apiKeyId);
      if (projectIds) {
        setInitialProjectIds(projectIds);
      } else if (
        store.rpcError === GetProjectsByAPIKeyRPC.RPCError.InvalidApiKey
      ) {
        await handleStaleKey();
      }
    } catch {
      closeSelectionDialog();
      setUnexpectedErrorMessage(
        t("externalPublicApis.apiKeyManagementGetProjectsUnexpectedError"),
      );
    } finally {
      setLoadingProjectAssignments(false);
    }
  };

  const handleConfirmedAction = async (): Promise<void> => {
    if (!confirmAction) return;
    try {
      if (confirmAction.action === "rotate") {
        const rotatedCredentials = await store.rotateAPIKey(
          confirmAction.apiKeyId,
        );
        if (rotatedCredentials) {
          setConfirmAction(undefined);
          setCredentials(rotatedCredentials);
        } else if (
          store.rpcError === InvalidateAndRotateAPIKeyRPC.RPCError.InvalidApiKey
        ) {
          await handleStaleKey();
        }
        return;
      }

      const deleted = await store.deleteAPIKey(confirmAction.apiKeyId);
      if (deleted) {
        setConfirmAction(undefined);
        await store.refreshCurrentPageAfterDeletion();
      } else if (store.rpcError === DeleteAPIKeyRPC.RPCError.InvalidApiKey) {
        await handleStaleKey();
      }
    } catch {
      setUnexpectedErrorMessage(
        t(
          confirmAction.action === "rotate"
            ? "externalPublicApis.apiKeyManagementRotateUnexpectedError"
            : "externalPublicApis.apiKeyManagementDeleteUnexpectedError",
        ),
      );
    }
  };

  const mainHeaderProps: HeaderProps = {
    title: t("externalPublicApis.apiKeyManagementTitle"),
    prefersLargeTitle: true,
    backButtonCallback: navigateTo.project,
    actionElement: (
      <Button
        label={t("externalPublicApis.apiKeyManagementGenerateNewKey")}
        name="generateNewAPIKey"
        size="medium"
        variant="filled"
        icon={<Plus />}
        onClick={() => {
          setInitialProjectIds([]);
          setSelectionMode("generate");
        }}
      />
    ),
  };

  const networkErrorPage = navigateOrGetNetworkErrorPage(
    networkingErrorStore.error,
    navigateTo,
    networkingErrorStore.removeError,
  );
  if (networkErrorPage) {
    networkingErrorStore.removeError();
    return networkErrorPage;
  }
  if (!hasPrivilege) return <></>;

  const emptyStateButtonProps: ButtonProps = {
    name: "generateNewAPIKey",
    label: t("externalPublicApis.apiKeyManagementGenerateNewKey"),
    variant: "outlined-color",
    size: "small",
    icon: <Plus />,
    onClick: () => setSelectionMode("generate"),
  };

  return (
    <>
      <DetailSectionComponent
        mainHeaderProps={mainHeaderProps}
        isOverflowHidden={false}
        isChildHeightVariable={false}
        isManageUsersLogoVisible
        isAuditLogsLogoVisible
        isEmailTemplatesLogoVisible
        isAllowedDomainsLogoVisible
        isManageKeysLogoVisible={false}
      >
        <Stack
          justifyContent="start"
          alignItems="center"
          height="100%"
          width="100%"
          padding={spacing.spaceXL}
        >
          {store.loadingAPIKeys || store.loadingProjects ? (
            <LoadingIndicator
              variant="container"
              loadingText={t(
                store.loadingAPIKeys
                  ? "externalPublicApis.apiKeyManagementLoadingKeys"
                  : "externalPublicApis.apiKeyManagementLoadingProjects",
              )}
              isLoading
            />
          ) : store.apiKeys.length === 0 ? (
            <Stack justifyContent="center" height="100%">
              <EmptyState
                icon={<KeyRound />}
                title={t("externalPublicApis.apiKeyManagementEmptyStateTitle")}
                description={t(
                  "externalPublicApis.apiKeyManagementEmptyStateDescription",
                )}
                width="100%"
                buttonProps={emptyStateButtonProps}
              />
            </Stack>
          ) : (
            <APIKeysTable
              onEditAssignments={(apiKey) =>
                void handleEditAssignments(apiKey.apiKeyId)
              }
              onRotate={(apiKey) =>
                setConfirmAction({
                  action: "rotate",
                  apiKeyId: apiKey.apiKeyId,
                })
              }
              onDelete={(apiKey) =>
                setConfirmAction({
                  action: "delete",
                  apiKeyId: apiKey.apiKeyId,
                })
              }
            />
          )}
        </Stack>
      </DetailSectionComponent>

      {selectionMode && (
        <APIKeyProjectSelectionDialog
          open
          mode={selectionMode}
          apiKeyId={editingAPIKeyId}
          initialProjectIds={initialProjectIds}
          loadingInitialProjects={loadingProjectAssignments}
          onClose={closeSelectionDialog}
          onGenerated={(generatedCredentials) => {
            closeSelectionDialog();
            setCredentials(generatedCredentials);
          }}
          onUpdated={() => {
            closeSelectionDialog();
            void store.refreshCurrentPage();
          }}
          onStaleKey={() => void handleStaleKey()}
          onInvalidProject={() => setInformationalError("invalidProject")}
          onUnexpectedError={setUnexpectedErrorMessage}
        />
      )}

      {confirmAction && (
        <ConfirmAPIKeyActionDialog
          {...confirmAction}
          loading={store.loadingAction}
          onConfirm={handleConfirmedAction}
          onClose={() => {
            setConfirmAction(undefined);
            store.clearRPCError();
          }}
        />
      )}

      {credentials && (
        <OneTimeCredentialsDialog
          credentials={credentials}
          onDone={() => {
            setCredentials(undefined);
            void store.refreshCurrentPage();
          }}
        />
      )}

      {informationalError && (
        <ErrorDialog
          title={t(
            informationalError === "staleKey"
              ? "externalPublicApis.apiKeyManagementStaleKeyTitle"
              : "externalPublicApis.apiKeyManagementReviewProjectsTitle",
          )}
          message={t(
            informationalError === "staleKey"
              ? "externalPublicApis.apiKeyManagementStaleKeyError"
              : "externalPublicApis.apiKeyManagementInvalidProjectError",
          )}
          onClickClose={() => setInformationalError(undefined)}
        />
      )}

      {(unexpectedErrorMessage !== undefined ? true : initialLoadFailed) && (
        <UnexpectedErrorDialog
          message={
            unexpectedErrorMessage ??
            t("externalPublicApis.apiKeyManagementGetKeysUnexpectedError")
          }
          onClickDone={() => {
            if (initialLoadFailed) {
              navigateTo.project();
              return;
            }
            setUnexpectedErrorMessage(undefined);
          }}
        />
      )}
    </>
  );
});
